import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Edit2, Trash2, TrendingUp, TrendingDown, 
  DollarSign, Target, BarChart3, Calendar, Filter, RefreshCw,
  Award, AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';

import { API } from '@/config/api';

const EMOTION_OPTIONS = [
  "Confident", "Anxious", "FOMO", "Greedy", "Fearful", 
  "Disciplined", "Impatient", "Calm", "Frustrated", "Excited"
];

const STRATEGY_OPTIONS = [
  "Breakout", "Trend Following", "Mean Reversion", "Scalping",
  "Swing Trade", "Position Trade", "News-Based", "Technical Analysis",
  "Fundamental Analysis", "Copy Trade"
];

export default function TradeJournal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterSide, setFilterSide] = useState('all');
  
  const [formData, setFormData] = useState({
    symbol: '',
    side: 'buy',
    entry_price: '',
    exit_price: '',
    quantity: '',
    strategy: '',
    notes: '',
    emotions: '',
    lessons_learned: ''
  });
  
  const fetchJournal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/trade-journal`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setStats(data.stats || null);
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchJournal();
  }, [fetchJournal]);
  
  const resetForm = () => {
    setFormData({
      symbol: '',
      side: 'buy',
      entry_price: '',
      exit_price: '',
      quantity: '',
      strategy: '',
      notes: '',
      emotions: '',
      lessons_learned: ''
    });
    setEditingEntry(null);
  };
  
  const handleSubmit = async () => {
    if (!formData.symbol || !formData.entry_price || !formData.quantity) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const url = editingEntry 
        ? `${API}/trade-journal/${editingEntry.journal_id}`
        : `${API}/trade-journal`;
      
      const method = editingEntry ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          entry_price: parseFloat(formData.entry_price),
          exit_price: formData.exit_price ? parseFloat(formData.exit_price) : null,
          quantity: parseFloat(formData.quantity)
        })
      });
      
      if (res.ok) {
        toast.success(editingEntry ? 'Entry updated' : 'Entry added');
        setModalOpen(false);
        resetForm();
        fetchJournal();
      } else {
        toast.error('Failed to save entry');
      }
    } catch (e) {
      toast.error('Error saving entry');
    }
  };
  
  const handleDelete = async (journalId) => {
    if (!window.confirm('Delete this journal entry?')) return;
    
    try {
      const res = await fetch(`${API}/trade-journal/${journalId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Entry deleted');
        fetchJournal();
      }
    } catch (e) {
      toast.error('Error deleting entry');
    }
  };
  
  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      symbol: entry.symbol,
      side: entry.side,
      entry_price: entry.entry_price?.toString() || '',
      exit_price: entry.exit_price?.toString() || '',
      quantity: entry.quantity?.toString() || '',
      strategy: entry.strategy || '',
      notes: entry.notes || '',
      emotions: entry.emotions || '',
      lessons_learned: entry.lessons_learned || ''
    });
    setModalOpen(true);
  };
  
  const filteredEntries = filterSide === 'all' 
    ? entries 
    : entries.filter(e => e.side === filterSide);
  
  const formatCurrency = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="trade-journal-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14120f] via-[#1c1814] to-[#14120f] border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                <BookOpen className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Trade Journal</h1>
                <p className="text-gray-400">Track, analyze, and improve your trading</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchJournal} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-[#D4AF37] text-black">
                <Plus className="h-4 w-4 mr-2" /> New Entry
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm">Total Trades</p>
                <p className="text-2xl font-bold text-white">{stats.total_trades}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm">Win Rate</p>
                <p className={`text-2xl font-bold ${stats.win_rate >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {stats.win_rate}%
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm">Winners</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.winning_trades}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm">Losers</p>
                <p className="text-2xl font-bold text-red-400">{stats.losing_trades}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <p className="text-gray-400 text-sm">Total P&L</p>
                <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {stats.total_pnl >= 0 ? '+' : ''}{formatCurrency(stats.total_pnl)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="text-gray-400 text-sm">Filter:</span>
          </div>
          <div className="flex gap-2">
            {['all', 'buy', 'sell'].map(side => (
              <Button
                key={side}
                size="sm"
                variant={filterSide === side ? 'default' : 'outline'}
                onClick={() => setFilterSide(side)}
                className={filterSide === side ? 'bg-[#D4AF37] text-black' : 'border-[#1c1814] text-gray-400'}
              >
                {side === 'all' ? 'All' : side === 'buy' ? 'Buys' : 'Sells'}
              </Button>
            ))}
          </div>
        </div>

        {/* Journal Entries */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading journal...</div>
        ) : filteredEntries.length === 0 ? (
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Journal Entries</h3>
              <p className="text-gray-400 mb-4">Start tracking your trades to improve your performance</p>
              <Button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-[#D4AF37] text-black">
                <Plus className="h-4 w-4 mr-2" /> Add First Entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map((entry) => (
              <Card key={entry.journal_id} className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${entry.side === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                        {entry.side === 'buy' ? 
                          <TrendingUp className="h-6 w-6 text-emerald-400" /> : 
                          <TrendingDown className="h-6 w-6 text-red-400" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white">{entry.symbol}</h3>
                          <Badge className={entry.side === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                            {entry.side.toUpperCase()}
                          </Badge>
                          {entry.strategy && (
                            <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37]">
                              {entry.strategy}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">
                          {new Date(entry.created_at).toLocaleDateString()} • {entry.quantity} shares
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Entry → Exit</p>
                        <p className="text-white">
                          {formatCurrency(entry.entry_price)} → {entry.exit_price ? formatCurrency(entry.exit_price) : '—'}
                        </p>
                      </div>
                      
                      {entry.pnl !== null && (
                        <div className="text-right">
                          <p className="text-sm text-gray-400">P&L</p>
                          <p className={`text-lg font-bold ${entry.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {entry.pnl >= 0 ? '+' : ''}{formatCurrency(entry.pnl)}
                            <span className="text-sm ml-1">({entry.pnl_percent}%)</span>
                          </p>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(entry)} className="text-gray-400 hover:text-[#D4AF37]">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.journal_id)} className="text-gray-400 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {(entry.notes || entry.emotions || entry.lessons_learned) && (
                    <div className="mt-4 pt-4 border-t border-[#1c1814] grid md:grid-cols-3 gap-4">
                      {entry.emotions && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-1">Emotions</p>
                          <p className="text-sm text-gray-300">{entry.emotions}</p>
                        </div>
                      )}
                      {entry.notes && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
                          <p className="text-sm text-gray-300">{entry.notes}</p>
                        </div>
                      )}
                      {entry.lessons_learned && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-1">Lessons</p>
                          <p className="text-sm text-gray-300">{entry.lessons_learned}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Symbol *</label>
                <Input
                  value={formData.symbol}
                  onChange={(e) => setFormData({...formData, symbol: e.target.value.toUpperCase()})}
                  placeholder="AAPL"
                  className="bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Side *</label>
                <Select value={formData.side} onValueChange={(v) => setFormData({...formData, side: v})}>
                  <SelectTrigger className="bg-[#0a0a0f] border-[#1c1814] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814]">
                    <SelectItem value="buy">Buy</SelectItem>
                    <SelectItem value="sell">Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Entry Price *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.entry_price}
                  onChange={(e) => setFormData({...formData, entry_price: e.target.value})}
                  placeholder="0.00"
                  className="bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Exit Price</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.exit_price}
                  onChange={(e) => setFormData({...formData, exit_price: e.target.value})}
                  placeholder="0.00"
                  className="bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Quantity *</label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  placeholder="0"
                  className="bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Strategy</label>
                <Select value={formData.strategy} onValueChange={(v) => setFormData({...formData, strategy: v})}>
                  <SelectTrigger className="bg-[#0a0a0f] border-[#1c1814] text-white">
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814]">
                    {STRATEGY_OPTIONS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Emotions</label>
                <Select value={formData.emotions} onValueChange={(v) => setFormData({...formData, emotions: v})}>
                  <SelectTrigger className="bg-[#0a0a0f] border-[#1c1814] text-white">
                    <SelectValue placeholder="How did you feel?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814]">
                    {EMOTION_OPTIONS.map(e => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Trade Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Why did you take this trade? What was your thesis?"
                className="bg-[#0a0a0f] border-[#1c1814] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Lessons Learned</label>
              <Textarea
                value={formData.lessons_learned}
                onChange={(e) => setFormData({...formData, lessons_learned: e.target.value})}
                placeholder="What did you learn from this trade?"
                className="bg-[#0a0a0f] border-[#1c1814] text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-[#D4AF37] text-black">
              {editingEntry ? 'Update Entry' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
